import sortedIndexBy from "lodash/sortedIndexBy";

/**
 * Throw an error with the provided message. This is useful as a helper for throwing an error after a null coalescing operator.
 * See: https://stackoverflow.com/a/65666402/1988326
 */
export function throwExpression(errorMessage: string): never {
  throw new Error(errorMessage);
}

/**
 * Performs a binary search of the provided sorted array using the specified function to extract the value to compare.
 *
 * See: https://stackoverflow.com/q/37711682/1988326
 */
export function sortedFindBy<THaystack, TNeedle = THaystack>(
  haystack: THaystack[],
  needle: TNeedle,
  iteratee: (x: THaystack | TNeedle) => unknown,
): THaystack | undefined {
  // Use a binary search to determine the lowest index at which the needle should be inserted into the haystack in order to maintain its sort order.
  const insertionIndex = sortedIndexBy(haystack, needle, iteratee);

  // There is no match found if the needle is greater than all haystack items
  // If the needle is equal to the last item, the index returned would have inserted it BEFORE the last item, not after it
  if (insertionIndex === haystack.length) return undefined;

  // If there is a match then the value at the insertion index will match the needle
  const potentialMatch = haystack[insertionIndex]!;
  return iteratee(potentialMatch) === iteratee(needle) ? potentialMatch : undefined;
}

/**
 * Attempt to extract a Google Drive file ID from a URL. This method supports the following URL formats for files that
 * can be found in Google Drive:
 *
 * - https://drive.google.com/file/d/<file_id>/view?usp=sharing
 * - https://drive.google.com/open?id=<file_id>
 * - https://docs.google.com/document/d/<file_id>/edit
 * - https://docs.google.com/spreadsheets/d/<file_id>/edit
 * - https://docs.google.com/presentation/d/<file_id>/edit
 * - https://docs.google.com/drawings/d/<file_id>/edit
 * - https://drive.google.com/uc?id=<file_id>
 */
export function tryExtractGoogleDriveId(url: string | URL): string | null {
  // Parse the URL if a plain string was provided
  if (typeof url === "string") {
    try {
      url = new URL(url);
    } catch {
      return null;
    }
  }

  // Only support http and https protocols
  if (url.protocol !== "http:" && url.protocol !== "https:") return null;

  // Now that the protocol is validated, we just use the host and path for matching
  const hostAndPath = url.host + url.pathname;

  // 1. /file/d/<id>
  let match = /^drive\.google\.com\/file\/d\/([a-z0-9_-]+)/i.exec(hostAndPath);
  if (match) return match[1]!;

  // 2. /open?id=<id> (with possible extra params)
  if (hostAndPath.startsWith("drive.google.com/open")) {
    const id = url.searchParams.get("id");
    if (id && /^[a-z0-9_-]+$/i.test(id)) return id;
  }

  // 3. /uc?id=<id> (with possible extra params)
  if (hostAndPath.startsWith("drive.google.com/uc")) {
    const id = url.searchParams.get("id");
    if (id && /^[A-Za-z0-9_-]+$/.test(id)) return id;
  }

  // 4. docs.google.com/(document|spreadsheets|presentation|drawings)/d/<id>
  match = /^docs\.google\.com\/(document|spreadsheets|presentation|drawings)(\/u\/\d)?\/d\/([a-z0-9_-]+)/i.exec(
    hostAndPath,
  );
  if (match) return match[3]!;

  return null;
}
